import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/pages/PlaceholderPage";

const emptyForm = { username: "", full_name: "", phone_number: "", role: "user", password: "", is_active: true };

const Field = ({ label, children, error }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700">{label}</Label>
    {children}
    {error && <p className="text-sm text-red-600" data-testid="user-form-error">{error}</p>}
  </div>
);

export default function ManageUsersPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const openAdd = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      full_name: user.full_name,
      phone_number: user.phone_number || "",
      role: user.role,
      password: "",
      is_active: user.is_active,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (editingUser) {
        const payload = {
          full_name: form.full_name,
          phone_number: form.phone_number || null,
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password) payload.password = form.password;
        return (await api.put(`/users/${editingUser.id}`, payload)).data;
      }
      return (
        await api.post("/users", {
          username: form.username.trim(),
          full_name: form.full_name,
          phone_number: form.phone_number || null,
          role: form.role,
          password: form.password,
        })
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(editingUser ? "User updated" : "User created");
      setDialogOpen(false);
    },
    onError: (e) => setFormError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const canSubmit = editingUser
    ? form.full_name.trim()
    : form.username.trim() && form.full_name.trim() && form.password;

  return (
    <div className="p-6 sm:p-8" data-testid="manage-users-page">
      <PageHeader title="Manage Users" subtitle="Staff accounts — only admins can see this section">
        <Button
          onClick={openAdd}
          data-testid="add-user-button"
          className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
        >
          <Plus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] overflow-hidden animate-fade-up">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>Username</TableHead>
              <TableHead>Full Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                  <TableCell className="font-medium text-emerald-950">{u.username}</TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell className="text-slate-500">{u.phone_number || "—"}</TableCell>
                  <TableCell>
                    <Badge className={u.role === "admin" ? "bg-emerald-900 hover:bg-emerald-900" : "bg-slate-200 text-slate-700 hover:bg-slate-200"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.is_active ? "border-emerald-300 text-emerald-800" : "border-red-200 text-red-600"} data-testid={`user-status-${u.username}`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`} className="text-slate-600 hover:text-emerald-900">
                      <Pencil className="h-4 w-4 mr-1.5" /> Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-emerald-950">
              {editingUser ? `Edit User — ${editingUser.username}` : "Add User"}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? "Update details, reset the password, or toggle account access."
                : "The password is shown in plain text — hand it to the staff member personally."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingUser && (
              <Field label="Username" error={formError.toLowerCase().includes("username") ? formError : ""}>
                <Input value={form.username} onChange={(e) => set("username")(e.target.value)} placeholder="e.g. rahul.s" data-testid="user-form-username" className="rounded-lg" />
              </Field>
            )}
            <Field label="Full Name">
              <Input value={form.full_name} onChange={(e) => set("full_name")(e.target.value)} placeholder="Full name" data-testid="user-form-fullname" className="rounded-lg" />
            </Field>
            <Field label="Phone Number">
              <Input value={form.phone_number} onChange={(e) => set("phone_number")(e.target.value)} placeholder="Optional" data-testid="user-form-phone" className="rounded-lg" />
            </Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="user-form-role" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={editingUser ? "Reset Password (leave blank to keep current)" : "Password"}>
              <Input type="text" value={form.password} onChange={(e) => set("password")(e.target.value)} placeholder={editingUser ? "New password" : "Type a password to hand to the staff member"} data-testid="user-form-password" className="rounded-lg font-mono" />
            </Field>
            {editingUser && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Active</p>
                  <p className="text-xs text-slate-500">Inactive users cannot log in</p>
                </div>
                <Switch checked={form.is_active} onCheckedChange={set("is_active")} data-testid="user-form-active-toggle" />
              </div>
            )}
            {formError && !formError.toLowerCase().includes("username") && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="user-form-server-error">{formError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!canSubmit || mutation.isPending}
              data-testid="user-form-submit"
              className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white"
            >
              {mutation.isPending ? "Saving…" : editingUser ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
